import { useEffect, useState } from 'react';
import type { CustomFieldDefinitionResponse, CustomFieldEntityType, CustomFieldType } from '@orion/contracts';
import { Button, Switch } from '@orion/ui';
import { PageHeader } from '../../../app/shell.js';
import { useArchiveCustomField, useCreateCustomField, useCustomFieldsQuery, useReorderCustomFields, useUpdateCustomField } from './api.js';

const isDesignCustomFieldsPreview = import.meta.env.DEV && new URLSearchParams(window.location.search).get('__design') === 'AdminCustomFields';

const ENTITY_TABS: Array<{ value: CustomFieldEntityType; label: string }> = [
  { value: 'lead', label: 'Leads' },
  { value: 'contact', label: 'Contacts' },
  { value: 'company', label: 'Companies' },
  { value: 'opportunity', label: 'Opportunities' },
  { value: 'task', label: 'Tasks' },
];

const STANDARD_FIELDS: Record<CustomFieldEntityType, string> = {
  lead: '14 built-in · Name, Email, Phone, Company, Job title, Website, Status, Source, Owner, Score, Priority, Last contacted, Next follow-up, Converted at',
  contact: '9 built-in · Name, Email, Phone, Mobile, Job title, Department, Company, Owner, Lifecycle stage',
  company: '9 built-in · Name, Domain, Industry, Size, Phone, Website, Owner, Lifecycle stage, Customer since',
  opportunity: '11 built-in · Name, Pipeline, Stage, Amount, Currency, Probability, Expected close date, Company, Primary contact, Owner, Loss reason',
  task: '8 built-in · Title, Description, Kind, Status, Priority, Due date, Reminder, Owner',
};

const TYPE_LABEL: Record<CustomFieldType, string> = {
  text: 'Text',
  long_text: 'Long text',
  number: 'Number',
  currency: 'Currency',
  percentage: 'Percentage',
  date: 'Date',
  datetime: 'Date & time',
  boolean: 'Boolean',
  dropdown: 'Dropdown',
  multi_select: 'Multi-select',
  email: 'Email',
  phone: 'Phone',
  url: 'URL',
};

const TYPE_ORDER: CustomFieldType[] = ['text', 'long_text', 'number', 'currency', 'percentage', 'date', 'datetime', 'boolean', 'dropdown', 'multi_select', 'email', 'phone', 'url'];
const OPTIONS_TYPES = new Set<CustomFieldType>(['dropdown', 'multi_select']);

function TypeIcon({ type }: { type: CustomFieldType }) {
  const common = { width: 14, height: 14, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  switch (type) {
    case 'text':
      return (
        <svg {...common}>
          <path d="M5 6V4h14v2M12 4v16M9 20h6" />
        </svg>
      );
    case 'long_text':
      return (
        <svg {...common} strokeWidth={2.4}>
          <path d="M4 6h16M4 10h16M4 14h16M4 18h10" />
        </svg>
      );
    case 'number':
      return (
        <svg {...common}>
          <path d="M9 4 7 20M17 4l-2 16M4 9h16M3 15h16" />
        </svg>
      );
    case 'currency':
      return (
        <svg {...common}>
          <path d="M12 3v18" />
          <path d="M16 7.5c0-1.4-1.8-2.5-4-2.5s-4 1.1-4 2.8 1.8 2.4 4 2.9 4 1.3 4 3.1-1.8 2.9-4 2.9-4-1.1-4-2.6" />
        </svg>
      );
    case 'percentage':
      return (
        <svg {...common}>
          <path d="M19 5 5 19" />
          <circle cx="7" cy="7" r="2.5" />
          <circle cx="17" cy="17" r="2.5" />
        </svg>
      );
    case 'date':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="16" rx="2" />
          <path d="M16 3v4M8 3v4M3 10h18" />
        </svg>
      );
    case 'datetime':
      return (
        <svg {...common}>
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
      );
    case 'boolean':
      return (
        <svg {...common}>
          <rect x="2" y="7" width="20" height="10" rx="5" />
          <circle cx="16" cy="12" r="2.5" />
        </svg>
      );
    case 'dropdown':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="m9 11 3 3 3-3" />
        </svg>
      );
    case 'multi_select':
      return (
        <svg {...common} strokeWidth={2.4}>
          <path d="m4 7 2 2 3-3M4 16l2 2 3-3M13 8h7M13 17h7" />
        </svg>
      );
    case 'email':
      return (
        <svg {...common}>
          <rect x="3" y="5" width="18" height="14" rx="2" />
          <path d="m3 7 9 6 9-6" />
        </svg>
      );
    case 'phone':
      return (
        <svg {...common}>
          <path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" />
        </svg>
      );
    case 'url':
      return (
        <svg {...common}>
          <path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1" />
          <path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" />
        </svg>
      );
  }
}

function toKey(label: string): string {
  const slug = label
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
  return `cf_${slug || 'field'}`;
}

interface DrawerState {
  entityType: CustomFieldEntityType;
  editing: CustomFieldDefinitionResponse | null;
}

function FieldDrawer({ state, onClose }: { state: DrawerState; onClose: () => void }) {
  const { editing, entityType } = state;
  const create = useCreateCustomField();
  const update = useUpdateCustomField();
  const archive = useArchiveCustomField();
  const isPreviewNewField = isDesignCustomFieldsPreview && !editing;

  const [fieldType, setFieldType] = useState<CustomFieldType>(editing?.fieldType ?? (isPreviewNewField ? 'dropdown' : 'text'));
  const [label, setLabel] = useState(editing?.label ?? (isPreviewNewField ? 'Industry segment' : ''));
  const [options, setOptions] = useState<string[]>(editing?.options ?? (isPreviewNewField ? ['Logistics', 'Healthcare', 'Construction', 'Software'] : []));
  const [required, setRequired] = useState(editing?.required ?? false);
  const [showOnCreate, setShowOnCreate] = useState(editing?.showOnCreate ?? true);
  const [isFilterable, setIsFilterable] = useState(editing?.isFilterable ?? (isPreviewNewField ? true : false));
  const [helpText, setHelpText] = useState(editing?.helpText ?? (isPreviewNewField ? 'Primary industry the company operates in.' : ''));
  const saving = create.isPending || update.isPending;

  const save = async () => {
    if (!label.trim()) return;
    if (editing) {
      await update.mutateAsync({ id: editing.id, input: { label, options: OPTIONS_TYPES.has(fieldType) ? options : undefined, required, showOnCreate, isFilterable, helpText: helpText || undefined } });
    } else {
      await create.mutateAsync({
        entityType,
        key: toKey(label),
        label,
        fieldType,
        options: OPTIONS_TYPES.has(fieldType) ? options : undefined,
        required,
        showOnCreate,
        isFilterable,
        helpText: helpText || undefined,
      });
    }
    onClose();
  };

  return (
    <>
      <div aria-hidden="true" className="fixed inset-0 z-40 bg-[#12151b]/42" onClick={onClose} />
      <aside role="dialog" aria-modal="true" aria-labelledby="nf-title" className="fixed inset-y-0 right-0 z-50 flex w-90 flex-col border-l border-border bg-surface shadow-dialog">
        <div className="flex h-16 shrink-0 items-center justify-between border-b border-border px-5">
          <div className="flex flex-col gap-px">
            <span id="nf-title" className="text-[17px] font-[650]">
              {editing ? 'Edit field' : 'New field'}
            </span>
            <span className="text-xs text-text-muted">on {ENTITY_TABS.find((t) => t.value === entityType)?.label}</span>
          </div>
          <button type="button" aria-label="Close" onClick={onClose} className="flex size-8 items-center justify-center rounded-chip hover:bg-surface-subtle">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6 6 18" />
            </svg>
          </button>
        </div>

        <div className="flex flex-1 flex-col gap-3.5 overflow-y-auto px-5 py-4">
          <fieldset>
            <legend className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Field type</legend>
            <div role="radiogroup" aria-label="Field type" className="grid grid-cols-3 gap-1.5">
              {TYPE_ORDER.map((t) => (
                <button
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={fieldType === t}
                  disabled={!!editing}
                  onClick={() => setFieldType(t)}
                  className={`flex h-8 items-center gap-1.5 whitespace-nowrap rounded-control border px-1.5 text-xs font-semibold disabled:opacity-60 ${
                    fieldType === t ? 'border-[1.5px] border-primary bg-primary-soft text-primary' : 'border-border text-text-secondary'
                  }`}
                >
                  <TypeIcon type={t} />
                  {TYPE_LABEL[t]}
                </button>
              ))}
            </div>
          </fieldset>

          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
            Label
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              className="h-9 rounded-input border border-primary px-3 text-sm font-normal text-ink shadow-focus-ring outline-none"
            />
            <span className="text-xs font-medium text-text-muted">
              API key <span className="font-mono text-[11px] text-text-secondary">{editing ? editing.key : toKey(label)}</span>
            </span>
          </label>

          {OPTIONS_TYPES.has(fieldType) && (
            <fieldset className="flex flex-col gap-1.5">
              <legend className="mb-1.5 text-[13px] font-semibold">Options</legend>
              <ul className="overflow-hidden rounded-control border border-border">
                {options.map((opt, i) => (
                  <li key={i} className="flex h-8 items-center gap-1.5 border-b border-divider px-2 last:border-b-0">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" aria-hidden="true" className="text-text-faint">
                      <circle cx="9" cy="6" r="0.8" fill="currentColor" />
                      <circle cx="15" cy="6" r="0.8" fill="currentColor" />
                      <circle cx="9" cy="12" r="0.8" fill="currentColor" />
                      <circle cx="15" cy="12" r="0.8" fill="currentColor" />
                      <circle cx="9" cy="18" r="0.8" fill="currentColor" />
                      <circle cx="15" cy="18" r="0.8" fill="currentColor" />
                    </svg>
                    <input
                      aria-label={`Option ${i + 1}`}
                      value={opt}
                      onChange={(e) => setOptions(options.map((o, j) => (j === i ? e.target.value : o)))}
                      className="flex-1 text-[13px] outline-none"
                    />
                    <button type="button" aria-label={`Remove ${opt}`} onClick={() => setOptions(options.filter((_, j) => j !== i))} className="flex size-6 items-center justify-center rounded-chip hover:bg-surface-subtle">
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" aria-hidden="true">
                        <path d="M6 6l12 12M18 6 6 18" />
                      </svg>
                    </button>
                  </li>
                ))}
                <li className="flex h-8 items-center px-2">
                  <button type="button" onClick={() => setOptions([...options, ''])} className="flex items-center gap-1.5 text-[13px] font-semibold text-primary">
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    Add option
                  </button>
                </li>
              </ul>
            </fieldset>
          )}

          <div className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-medium">Required</span>
              <Switch checked={required} onChange={setRequired} label="Required" hideLabel />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-medium">Show on create form</span>
              <Switch checked={showOnCreate} onChange={setShowOnCreate} label="Show on create form" hideLabel />
            </div>
            <div className="flex items-center justify-between">
              <span className="text-[13px] font-medium">Filterable</span>
              <Switch checked={isFilterable} onChange={setIsFilterable} label="Filterable" hideLabel />
            </div>
          </div>

          <label className="flex flex-col gap-1.5 text-[13px] font-semibold text-ink">
            Help text <span className="-mt-1 text-xs font-normal text-text-muted">Shown under the field on forms</span>
            <input type="text" value={helpText} onChange={(e) => setHelpText(e.target.value)} className="h-9 rounded-input border border-border-strong px-3 text-sm font-normal outline-none" />
          </label>

          {entityType === 'task' && !editing && (
            <div className="flex items-center gap-2 rounded-control bg-surface-subtle px-2.5 py-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="shrink-0 text-text-secondary">
                <circle cx="12" cy="12" r="9" />
                <path d="M12 11v5M12 8h.01" />
              </svg>
              <span className="text-xs leading-4 text-text-secondary">Tasks don&rsquo;t store custom field values yet — this defines the field, but it won&rsquo;t appear on a task until that&rsquo;s built.</span>
            </div>
          )}

          <div className="flex items-center gap-2 rounded-control bg-success-soft px-2.5 py-2">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-success">
              <path d="m5 12 5 5 9-10" />
            </svg>
            <span className="text-xs font-medium text-success">Fields are added instantly — no downtime or migrations.</span>
          </div>

          {editing && (
            <button
              type="button"
              disabled={editing.usageCount > 0}
              title={editing.usageCount > 0 ? `${editing.usageCount} records use this field` : undefined}
              onClick={async () => {
                await archive.mutateAsync(editing.id);
                onClose();
              }}
              className="self-start text-[13px] font-semibold text-danger disabled:text-text-faint"
            >
              Archive field
            </button>
          )}
        </div>

        <div className="flex h-16 shrink-0 items-center justify-end gap-2 border-t border-border px-5">
          <Button variant="secondary" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" loading={saving} onClick={save}>
            {editing ? 'Save' : 'Create field'}
          </Button>
        </div>
      </aside>
    </>
  );
}

function FieldsTable({ entityType, onEdit }: { entityType: CustomFieldEntityType; onEdit: (field: CustomFieldDefinitionResponse) => void }) {
  const { data: fields } = useCustomFieldsQuery(entityType);
  const update = useUpdateCustomField();
  const reorder = useReorderCustomFields();
  const rows = [...(fields ?? [])].sort((a, b) => a.position - b.position);

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= rows.length) return;
    const orderedIds = rows.map((f) => f.id);
    [orderedIds[index], orderedIds[target]] = [orderedIds[target]!, orderedIds[index]!];
    reorder.mutate({ entityType, orderedIds });
  };

  if (rows.length === 0) {
    return (
      <section aria-label={`${entityType} custom fields`} className="flex flex-col items-center justify-center gap-1 rounded-dialog border border-dashed border-border-strong bg-surface p-8 text-center shadow-card">
        <p className="text-sm text-text-muted">No custom fields on this object yet.</p>
      </section>
    );
  }

  return (
    <section aria-label={`${entityType} custom fields`} className="overflow-hidden rounded-dialog border border-border bg-surface shadow-card">
      <div
        className="grid h-9 items-center gap-2 px-4 text-xs font-semibold text-text-muted"
        style={{ gridTemplateColumns: '28px 1.2fr 1.3fr 1fr 72px 104px 80px 100px 40px', background: 'var(--color-surface-subtle)', borderBottom: '1px solid var(--color-border)' }}
      >
        <span />
        <span>Label</span>
        <span>API key</span>
        <span>Type</span>
        <span className="text-center">Required</span>
        <span className="text-center">Show on create</span>
        <span className="text-center">Filterable</span>
        <span>Used in</span>
        <span />
      </div>
      {rows.map((field, i) => (
        <div
          key={field.id}
          className="grid min-h-12 items-center gap-2 border-b border-divider px-4 text-sm last:border-b-0"
          style={{ gridTemplateColumns: '28px 1.2fr 1.3fr 1fr 72px 104px 80px 100px 40px' }}
        >
          <button type="button" onClick={() => move(i, -1)} aria-label={`Move ${field.label} up`} className="flex text-text-faint hover:text-text-secondary">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" aria-hidden="true">
              <circle cx="9" cy="6" r="0.8" fill="currentColor" />
              <circle cx="15" cy="6" r="0.8" fill="currentColor" />
              <circle cx="9" cy="12" r="0.8" fill="currentColor" />
              <circle cx="15" cy="12" r="0.8" fill="currentColor" />
              <circle cx="9" cy="18" r="0.8" fill="currentColor" />
              <circle cx="15" cy="18" r="0.8" fill="currentColor" />
            </svg>
          </button>
          <span className="font-semibold">{field.label}</span>
          <span className="font-mono text-xs text-text-secondary">{field.key}</span>
          <span className="flex items-center gap-2 text-[13px] text-text-secondary">
            <span className="flex size-6 items-center justify-center rounded-chip bg-surface-subtle text-text-secondary">
              <TypeIcon type={field.fieldType} />
            </span>
            {TYPE_LABEL[field.fieldType]}
          </span>
          <span className="flex justify-center">
            <Switch checked={field.required} onChange={(v) => update.mutate({ id: field.id, input: { required: v } })} label={`${field.label} required`} hideLabel />
          </span>
          <span className="flex justify-center">
            <Switch checked={field.showOnCreate} onChange={(v) => update.mutate({ id: field.id, input: { showOnCreate: v } })} label={`${field.label} shown on create`} hideLabel />
          </span>
          <span className="flex justify-center">
            <Switch checked={field.isFilterable} onChange={(v) => update.mutate({ id: field.id, input: { isFilterable: v } })} label={`${field.label} filterable`} hideLabel />
          </span>
          <span className="tabular-nums text-[13px] text-text-secondary">{field.usageCount} records</span>
          <button type="button" aria-label={`Edit ${field.label}`} onClick={() => onEdit(field)} className="flex size-7 items-center justify-center rounded-chip text-text-secondary hover:bg-surface-subtle">
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M4 20h4L19 9l-4-4L4 16z" />
              <path d="m13 7 4 4" />
            </svg>
          </button>
        </div>
      ))}
    </section>
  );
}

function StandardFieldsCard({ entityType }: { entityType: CustomFieldEntityType }) {
  const [expanded, setExpanded] = useState(false);
  const text = STANDARD_FIELDS[entityType];
  const [count, list = ''] = text.split(' · ');

  return (
    <div className="flex h-13 items-center gap-2.5 rounded-dialog border border-border bg-surface px-5 shadow-card">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className="text-text-secondary">
        <rect x="4" y="11" width="16" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 0 1 8 0v4" />
      </svg>
      <span className="text-sm font-semibold">Standard {ENTITY_TABS.find((t) => t.value === entityType)?.label.toLowerCase().replace(/s$/, '')} fields</span>
      <span className="flex-1 truncate text-[13px] text-text-muted">
        {count} built-in · {expanded ? list : `${list.split(', ').slice(0, 7).join(', ')}…`}
      </span>
      <button type="button" onClick={() => setExpanded((v) => !v)} className="shrink-0 text-[13px] font-semibold text-primary">
        {expanded ? 'Hide' : 'Show'}
      </button>
    </div>
  );
}

function useEntityFieldCounts(): Record<CustomFieldEntityType, number> {
  const lead = useCustomFieldsQuery('lead');
  const contact = useCustomFieldsQuery('contact');
  const company = useCustomFieldsQuery('company');
  const opportunity = useCustomFieldsQuery('opportunity');
  const task = useCustomFieldsQuery('task');
  return {
    lead: lead.data?.length ?? 0,
    contact: contact.data?.length ?? 0,
    company: company.data?.length ?? 0,
    opportunity: opportunity.data?.length ?? 0,
    task: task.data?.length ?? 0,
  };
}

export function CustomFieldsAdminPage() {
  const [entityType, setEntityType] = useState<CustomFieldEntityType>('lead');
  const [drawer, setDrawer] = useState<DrawerState | null>(null);
  const counts = useEntityFieldCounts();

  useEffect(() => {
    if (isDesignCustomFieldsPreview) setDrawer({ entityType: 'lead', editing: null });
  }, []);

  return (
    <>
      <PageHeader breadcrumb={[{ label: 'Administration', to: '/admin' }, { label: 'Custom fields' }]} />
      <main className="flex flex-col gap-4 overflow-hidden px-8 py-7" style={{ height: 'calc(100vh - 60px)' }}>
        <div className="flex items-start justify-between gap-6">
          <div className="flex flex-col gap-1">
            <h1 className="text-[22px] font-[650] leading-7 tracking-[-0.01em]">Custom fields</h1>
            <p className="text-sm leading-5 text-text-muted">Capture what&rsquo;s unique to your business. Fields appear on records, forms, filters, imports and the API.</p>
          </div>
          <button
            type="button"
            onClick={() => setDrawer({ entityType, editing: null })}
            className="flex h-9 shrink-0 items-center gap-1.5 rounded-control bg-primary px-3.5 text-sm font-semibold text-white"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" aria-hidden="true">
              <path d="M12 5v14M5 12h14" />
            </svg>
            New field
          </button>
        </div>

        <div role="tablist" aria-label="Object" className="flex gap-6 border-b border-border">
          {ENTITY_TABS.map((t) => (
            <button
              key={t.value}
              type="button"
              role="tab"
              aria-selected={entityType === t.value}
              onClick={() => setEntityType(t.value)}
              className={`flex h-10 items-center gap-1.5 border-b-2 text-sm ${entityType === t.value ? 'border-primary font-semibold text-ink' : 'border-transparent font-medium text-text-muted'}`}
            >
              {t.label}
              <span className={`flex h-4.5 items-center rounded-full px-1.5 text-[11px] font-semibold ${entityType === t.value ? 'bg-primary-soft text-primary' : 'bg-surface-subtle text-text-secondary'}`}>{counts[t.value]}</span>
            </button>
          ))}
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto">
          <FieldsTable entityType={entityType} onEdit={(field) => setDrawer({ entityType, editing: field })} />
          <StandardFieldsCard entityType={entityType} />
        </div>
      </main>
      {drawer && <FieldDrawer state={drawer} onClose={() => setDrawer(null)} />}
    </>
  );
}
